"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Zap, Target, Heart, Settings } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { TeamSection } from "@/components/team-section"
import { DualCTA } from "@/components/dual-cta"

export default function AboutPage() {
  const [mounted, setMounted] = useState(false)

  // Glassmorphism style object for reuse
  const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.21)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: '20px',
    border: '0.25px solid rgba(255, 255, 255, 0.1)',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(255, 255, 255, 0.1),
      inset 0 0 42px 21px rgba(255, 255, 255, 0.05)
    `
  }

  const GlassmorphismHighlights = () => (
    <>
      {/* Top highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)'
        }}
      />
      {/* Left highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '1px',
          height: '100%',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3))'
        }}
      />
    </>
  )

  useEffect(() => {
    setMounted(true)
  }, [])



  if (!mounted) {
    return null
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <Badge variant="outline" className="mb-4">Our Mission</Badge>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
              We help entrepreneurs turn ideas into 
              <span className="text-primary"> working prototypes</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Our mission is simple: get good ideas out of your head and into the world—before they die in a slide deck.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-4 bg-muted/20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                The Problem We&apos;re Solving
              </h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>Too many great ideas get stuck in planning mode.</p>
                <p>Endless decks, roadmaps, or funding delays—and nothing real ever gets built.</p>
                <p>We started The Rapid Prototyping Company because we&apos;ve been there. Whether you&apos;re an entrepreneur trying to validate an MVP or a team needing stakeholder buy-in, waiting months for a working version is too slow.</p>
                <p className="text-foreground font-semibold">We give you something real to test, show, or build on—in just a few weeks.</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center">
                <div className="text-6xl">🚀</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Values</h2>
            <p className="text-xl text-muted-foreground">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Zap,
                title: "Speed without shortcuts",
                description: "We build fast—but never at the expense of thoughtfulness, usability, or quality.",
                color: "text-primary"
              },
              {
                icon: Target,
                title: "Real beats perfect",
                description: "A working prototype in two weeks is worth more than a perfect plan six months from now.",
                color: "text-green-500"
              },
              {
                icon: Heart,
                title: "No fluff, no BS",
                description: "We&apos;ll challenge assumptions, flag red flags, and tell you if something needs work. We&apos;re partners, not order-takers.",
                color: "text-red-500"
              },
              {
                icon: Settings,
                title: "Transparency first",
                description: "Clear communication, honest feedback, and realistic expectations from day one.",
                color: "text-blue-500"
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="p-6 h-full hover:shadow-lg transition-shadow !bg-transparent relative overflow-hidden" style={glassmorphismStyle}>
                  <GlassmorphismHighlights />
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <value.icon className={`h-8 w-8 ${value.color}`} />
                      <h3 className="text-xl font-bold">{value.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 px-4 bg-muted/20">
        <TeamSection />
        <div className="text-center mt-12">
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We started this company because we were tired of good ideas dying in PowerPoint decks. 
            Now we&apos;re pioneering the future with AI that could one day lead our company.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 !bg-transparent relative overflow-hidden text-center" style={glassmorphismStyle}>
            <GlassmorphismHighlights />
            <CardContent className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <h2 className="text-3xl md:text-4xl font-bold">
                  Ready to turn your idea into reality?
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Let&apos;s build something amazing together. From concept to working prototype in weeks.
                </p>
                <DualCTA />
              </motion.div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  )
}